/**
 * Multi-LLM Service
 * Unified abstraction for Claude, OpenAI GPT, and Gemini
 */

import { recordUsage } from './tokenStats'

// Provider configurations (Updated December 2025)
// NOTE: maxTokens = OUTPUT tokens limit (response size)
// Context window (input) is much larger (200K+ for most models)
export const PROVIDERS = {
  claude: {
    name: 'Claude',
    models: {
      // Latest models (recommended)
      'opus-4.5': 'claude-opus-4-5-20251101',     // Most capable - November 2025
      'opus': 'claude-opus-4-5-20251101',         // Alias for latest opus
      'sonnet-4.5': 'claude-sonnet-4-5-20250929', // Best for most tasks
      'sonnet': 'claude-sonnet-4-5-20250929',     // Alias for latest sonnet
      'haiku': 'claude-3-5-haiku-20241022',       // Fast & cheap
      // Legacy (for compatibility)
      'sonnet-4': 'claude-sonnet-4-20250514',
      'opus-4': 'claude-opus-4-20250514',
    },
    endpoint: 'https://api.anthropic.com/v1/messages',
    maxTokens: 16384, // Claude 4+ supports up to 64K output, we use 16K for safety
  },
  openai: {
    name: 'OpenAI',
    models: {
      // Latest models (November 2025) - GPT-5.1 series
      'gpt-5.1': 'gpt-5.1',               // Latest frontier model (Nov 2025)
      'gpt-5.1-codex': 'gpt-5.1-codex',   // Optimized for agentic coding
      'gpt-5': 'gpt-5',                   // Released August 2025
      // Previous generation (still good)
      'gpt-4.1': 'gpt-4.1',               // April 2025
      'gpt-4o': 'gpt-4o',                 // May 2024, still capable
      'gpt-4o-mini': 'gpt-4o-mini',       // Budget option
      // Legacy alias
      'gpt-4': 'gpt-4o',                  // Alias for compatibility
    },
    endpoint: 'https://api.openai.com/v1/chat/completions',
    maxTokens: 16384, // GPT-5.1 supports large outputs
  },
  gemini: {
    name: 'Gemini',
    models: {
      // Latest models (November 2025) - Gemini 3.0!
      'pro': 'gemini-3.0-pro',              // Latest, beats GPT-5 Pro on benchmarks
      'deep-think': 'gemini-3.0-deep-think', // Advanced reasoning model
      '3.0-pro': 'gemini-3.0-pro',
      // Previous generation (still excellent)
      'flash': 'gemini-2.5-flash',           // Fast & stable
      '2.5-pro': 'gemini-2.5-pro',           // March 2025
      '2.5-flash': 'gemini-2.5-flash',
      'flash-lite': 'gemini-2.5-flash-lite', // Lightweight & cheap
      // Legacy (2.0 still works)
      '2.0-flash': 'gemini-2.0-flash',
    },
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/models',
    maxTokens: 32768, // Gemini 3.0 supports 32K+ output (was 8K - too low for large sites)
  },
}

/**
 * Get stored API key for a provider
 */
export function getApiKey(provider) {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  return keys[provider] || null
}

/**
 * Store API key for a provider (encrypted in production)
 */
export function setApiKey(provider, key) {
  const keys = JSON.parse(localStorage.getItem('emd_api_keys') || '{}')
  keys[provider] = key
  localStorage.setItem('emd_api_keys', JSON.stringify(keys))
}

/**
 * Check if a provider is configured
 */
export function isProviderConfigured(provider) {
  return !!getApiKey(provider)
}

/**
 * Get available providers (those with API keys configured)
 */
export function getAvailableProviders() {
  return Object.keys(PROVIDERS).filter(isProviderConfigured)
}

/**
 * Estimate token count (rough approximation)
 */
export function estimateTokens(text) {
  // Rough estimate: 1 token ≈ 4 characters for English, 2-3 for French
  return Math.ceil(text.length / 3.5)
}

/**
 * Estimate cost for a request
 */
export function estimateCost(provider, model, inputTokens, outputTokens = 1000) {
  // Pricing per 1M tokens (Updated December 2025)
  // Note: Prices may vary, check provider docs for latest
  const pricing = {
    claude: {
      'opus-4.5': { input: 5, output: 25 },     // Opus 4.5 - Nov 2025, much cheaper!
      'opus': { input: 5, output: 25 },         // Alias
      'sonnet-4.5': { input: 3, output: 15 },   // Sonnet 4.5
      'sonnet': { input: 3, output: 15 },
      'sonnet-4': { input: 3, output: 15 },
      'opus-4': { input: 15, output: 75 },      // Legacy Opus 4
      'haiku': { input: 0.25, output: 1.25 },
    },
    openai: {
      'gpt-5.1': { input: 5, output: 20 },      // Latest
      'gpt-5.1-codex': { input: 5, output: 20 },
      'gpt-5': { input: 5, output: 15 },
      'gpt-4.1': { input: 2, output: 8 },
      'gpt-4o': { input: 2.5, output: 10 },
      'gpt-4o-mini': { input: 0.15, output: 0.6 },
      'gpt-4': { input: 2.5, output: 10 },      // Alias
    },
    gemini: {
      'pro': { input: 1.5, output: 6 },          // Gemini 3.0 Pro
      '3.0-pro': { input: 1.5, output: 6 },
      'deep-think': { input: 2, output: 8 },     // Gemini 3.0 Deep Think
      '2.5-pro': { input: 1.25, output: 5 },
      'flash': { input: 0.075, output: 0.3 },    // Gemini 2.5 Flash
      '2.5-flash': { input: 0.075, output: 0.3 },
      '2.0-flash': { input: 0.075, output: 0.3 },
      'flash-lite': { input: 0.04, output: 0.15 },
    },
  }

  const prices = pricing[provider]?.[model]
  if (!prices) return null

  const cost = (inputTokens * prices.input + outputTokens * prices.output) / 1_000_000
  return Math.round(cost * 10000) / 10000 // Round to 4 decimals
}

/**
 * Call Claude API
 */
async function callClaude(apiKey, model, messages, systemPrompt, options = {}) {
  const modelId = PROVIDERS.claude.models[model] || model

  const body = {
    model: modelId,
    max_tokens: options.maxTokens || PROVIDERS.claude.maxTokens,
    system: systemPrompt,
    messages: messages.map(m => ({
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: m.content,
    })),
  }

  // Add temperature if specified (0.0-1.0, default ~0.7)
  if (options.temperature !== undefined) {
    body.temperature = options.temperature
  }

  const response = await fetch(PROVIDERS.claude.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || `Claude API error: ${response.status}`)
  }

  const data = await response.json()
  return {
    content: data.content[0].text,
    usage: {
      inputTokens: data.usage.input_tokens,
      outputTokens: data.usage.output_tokens,
    },
    model: modelId,
    provider: 'claude',
  }
}

/**
 * Call OpenAI API
 */
async function callOpenAI(apiKey, model, messages, systemPrompt, options = {}) {
  const modelId = PROVIDERS.openai.models[model] || model

  const formattedMessages = [
    { role: 'system', content: systemPrompt },
    ...messages.map(m => ({
      role: m.role === 'assistant' ? 'assistant' : 'user',
      content: m.content,
    })),
  ]

  const body = {
    model: modelId,
    max_tokens: options.maxTokens || PROVIDERS.openai.maxTokens,
    messages: formattedMessages,
  }

  // Add temperature if specified (0.0-2.0, default 1.0)
  if (options.temperature !== undefined) {
    body.temperature = options.temperature
  }

  const response = await fetch(PROVIDERS.openai.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || `OpenAI API error: ${response.status}`)
  }

  const data = await response.json()
  return {
    content: data.choices[0].message.content,
    usage: {
      inputTokens: data.usage.prompt_tokens,
      outputTokens: data.usage.completion_tokens,
    },
    model: modelId,
    provider: 'openai',
  }
}

/**
 * Call Gemini API
 */
async function callGemini(apiKey, model, messages, systemPrompt, options = {}) {
  const modelId = PROVIDERS.gemini.models[model] || model
  const endpoint = `${PROVIDERS.gemini.endpoint}/${modelId}:generateContent?key=${apiKey}`

  // Convert messages to Gemini format
  const contents = messages.map(m => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.content }],
  }))

  // Prepend system prompt as first user message if present
  if (systemPrompt) {
    contents.unshift({
      role: 'user',
      parts: [{ text: `System: ${systemPrompt}` }],
    })
    // Add empty model response to maintain alternation
    if (contents.length > 1 && contents[1].role === 'user') {
      contents.splice(1, 0, { role: 'model', parts: [{ text: 'Understood.' }] })
    }
  }

  const generationConfig = {
    maxOutputTokens: options.maxTokens || PROVIDERS.gemini.maxTokens,
  }

  // Add temperature if specified (0.0-2.0, default ~0.9)
  if (options.temperature !== undefined) {
    generationConfig.temperature = options.temperature
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      contents,
      generationConfig,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error?.message || `Gemini API error: ${response.status}`)
  }

  const data = await response.json()
  const text = data.candidates[0].content.parts[0].text

  return {
    content: text,
    usage: {
      inputTokens: data.usageMetadata?.promptTokenCount || 0,
      outputTokens: data.usageMetadata?.candidatesTokenCount || 0,
    },
    model: modelId,
    provider: 'gemini',
  }
}

/**
 * Check if DEV_MODE is enabled
 * Enable via: localStorage.setItem('EMD_DEBUG', 'true')
 * Or automatically in Vite dev mode
 */
export function isDevMode() {
  try {
    // Check explicit setting first
    const debugSetting = localStorage.getItem('EMD_DEBUG')
    if (debugSetting === 'true') return true
    if (debugSetting === 'false') return false
    // Default: disabled (was previously auto-enabled in Vite dev mode)
    return false
  } catch {
    return false
  }
}

/**
 * Toggle debug mode
 */
export function toggleDebugMode(enabled) {
  localStorage.setItem('EMD_DEBUG', enabled ? 'true' : 'false')
  console.log(`🔧 [DEBUG] Mode ${enabled ? 'activé' : 'désactivé'}`)
  return enabled
}

// Expose to window for easy toggle in console
if (typeof window !== 'undefined') {
  window.EMD_DEBUG = {
    enable: () => toggleDebugMode(true),
    disable: () => toggleDebugMode(false),
    status: () => isDevMode(),
  }
}

/**
 * Deep Debug listener for capturing full prompts
 * Set via window.EMD_LLM_LISTENER = (data) => { ... }
 */
let llmListener = null

export function setLLMListener(callback) {
  llmListener = callback
}

export function clearLLMListener() {
  llmListener = null
}

/**
 * Main LLM call function
 * @param {Object} options
 * @param {string} options.provider - 'claude' | 'openai' | 'gemini'
 * @param {string} options.model - Model variant (e.g., 'sonnet', 'gpt-4o', 'pro')
 * @param {Array} options.messages - Conversation messages [{role, content}]
 * @param {string} options.systemPrompt - System/instruction prompt
 * @param {number} options.maxTokens - Max response tokens
 * @param {number} options.temperature - Creativity control (0.0-1.0, higher = more creative)
 * @param {string} options.phase - Pipeline phase for token tracking (optional)
 */
export async function callLLM({ provider, model, messages, systemPrompt, maxTokens, temperature, phase }) {
  const apiKey = getApiKey(provider)
  if (!apiKey) {
    throw new Error(`No API key configured for ${provider}`)
  }

  const options = { maxTokens, temperature }
  const debug = isDevMode()

  // Deep Debug: Send full prompts to listener BEFORE call
  if (llmListener) {
    llmListener({
      type: 'llm_request',
      provider,
      model,
      systemPrompt,
      userMessage: messages[0]?.content,
      timestamp: new Date().toISOString(),
    })
  }

  // Make the actual LLM call
  let response
  switch (provider) {
    case 'claude':
      response = await callClaude(apiKey, model, messages, systemPrompt, options)
      break
    case 'openai':
      response = await callOpenAI(apiKey, model, messages, systemPrompt, options)
      break
    case 'gemini':
      response = await callGemini(apiKey, model, messages, systemPrompt, options)
      break
    default:
      throw new Error(`Unknown provider: ${provider}`)
  }

  // Deep Debug: Send full response to listener AFTER call
  if (llmListener) {
    llmListener({
      type: 'llm_response',
      provider,
      model,
      content: response.content,
      usage: response.usage,
      timestamp: new Date().toISOString(),
    })
  }

  // Record token usage for cost tracking
  if (response.usage) {
    recordUsage({
      provider,
      model,
      phase,
      inputTokens: response.usage.inputTokens || 0,
      outputTokens: response.usage.outputTokens || 0,
    })
  }

  // DEV_MODE: Log to console
  if (debug) {
    console.group(`🤖 [LLM] ${provider}/${model}`)
    console.log('📤 SYSTEM:', systemPrompt?.substring(0, 300) + '...')
    console.log('📤 USER:', messages[0]?.content?.substring(0, 300) + '...')
    console.log('📥 RESPONSE:', response.content?.substring(0, 300) + '...')
    console.log('📊 TOKENS:', response.usage)
    console.groupEnd()
  }

  return response
}

/**
 * Stream LLM response (for future implementation)
 */
export async function* streamLLM(options) {
  // TODO: Implement streaming for each provider
  const response = await callLLM(options)
  yield response.content
}

export default {
  callLLM,
  streamLLM,
  getApiKey,
  setApiKey,
  isProviderConfigured,
  getAvailableProviders,
  estimateTokens,
  estimateCost,
  isDevMode,
  toggleDebugMode,
  PROVIDERS,
}
